import { useMemo, type ReactElement, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Message } from '@/src/elements/message'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'

import { IntentionCtaBlock } from '../IntentionCtaBlock'

export type TIntentionPrayCardProps = {
  intention: {
    hasPrayed?: boolean
    content: string
  }
  onPray: VoidFunction
  testIdPrayButton: string
  title: Record<'default' | 'prayed', string | ReactElement | ReactNode>
}

type TValuesBasedOnPrayedType = {
  title: string | ReactElement | ReactNode
  color: 'primary' | 'success'
  contrast: 'default' | 'high'
}

export const IntentionPrayCard = ({
  intention,
  testIdPrayButton,
  onPray,
  title,
}: TIntentionPrayCardProps): ReactNode => {
  const valuesBasedOnPrayed: TValuesBasedOnPrayedType = useMemo(
    () =>
      intention.hasPrayed
        ? {
            title: title.prayed,
            color: 'success',
            contrast: 'default',
          }
        : {
            title: title.default,
            color: 'primary',
            contrast: 'high',
          },
    [intention.hasPrayed, title],
  )

  return (
    <IntentionCtaBlock isSuccess={intention.hasPrayed}>
      <Typography bold size="lg" colors={valuesBasedOnPrayed.color} contrast="high">
        {valuesBasedOnPrayed.title}
      </Typography>
      <View className="rounded-lg bg-white/75 p-4">
        <Typography colors={valuesBasedOnPrayed.color} contrast={valuesBasedOnPrayed.contrast}>
          {intention.content}
        </Typography>
      </View>
      {intention?.hasPrayed ? (
        <Message colors="success" iconName={ICON.CHECK_CIRCLE_BROKEN}>
          <FormattedMessage id="intention.prayed.already" /> <FormattedMessage id="intention.prayed.thanks" />
        </Message>
      ) : (
        <Button
          testID={testIdPrayButton}
          colors="primary"
          variant="solid"
          fullWidth
          startIcon={ICON.FIRE}
          onPress={onPray}
        >
          <FormattedMessage id="pray.i-pray.short" />
        </Button>
      )}
    </IntentionCtaBlock>
  )
}
